import type { WorldObjectDef } from '@/world-prototype/objects/placeObject';

/**
 * Declarative Spanish Fork backyard prop pack.
 *
 * Objects: freestanding circular gazebo, swingset, grill, porch, small bridge,
 * 5 large trees, 8 bushes. Layouts: `layouts.desktop` / `layouts.tablet` /
 * `layouts.mobile`. Flat fields mirror desktop. Independent of Hubert and
 * Holly Springs. Updated by World Editor (?edit=1).
 */
const PROP = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/props`;
const SHARED_PROP = `${import.meta.env.BASE_URL}art/environments/shared/props`;

export const SPANISH_FORK_PROP_SPRITE_BASES = {
  PROP,
  SHARED_PROP,
} as const;

const TREE_LEFT_SPRITE = {
  src: `${PROP}/tree-left-v1.png`,
  alt: 'Large backyard leafy tree',
  imageWidth: 1024,
  imageHeight: 1385,
} as const;

const TREE_RIGHT_SPRITE = {
  src: `${PROP}/tree-right-v1.png`,
  alt: 'Large backyard leafy tree',
  imageWidth: 997,
  imageHeight: 1388,
} as const;

const TREE_FOOTPRINT = [
  { x: -0.012, y: 0.003 },
  { x: 0.012, y: 0.003 },
  { x: 0.01, y: -0.012 },
  { x: -0.01, y: -0.012 },
] as const;

const BUSH_FOOTPRINT = [
  { x: -0.01, y: 0.004 },
  { x: 0.01, y: 0.004 },
  { x: 0.008, y: -0.008 },
  { x: -0.008, y: -0.008 },
] as const;

function bushDef(
  id: string,
  opts: {
    title: string;
    x: number;
    y: number;
    variant: 1 | 2;
    flipX?: boolean;
    scale?: number;
  },
): WorldObjectDef {
  const sprite =
    opts.variant === 1
      ? {
          src: `${SHARED_PROP}/bush-v1.png`,
          alt: 'Small leafy backyard bush',
          imageWidth: 886,
          imageHeight: 773,
        }
      : {
          src: `${SHARED_PROP}/bush-v2.png`,
          alt: 'Small leafy backyard bush',
          imageWidth: 948,
          imageHeight: 735,
        };
  const scale = opts.scale ?? 1;
  return {
    id,
    title: opts.title,
    icon: '🌿',
    actionLabel: 'Look',
    shape: 'hammock',
    interactive: false,
    enabled: true,
    x: opts.x,
    y: opts.y,
    widthFrac: 0.06,
    flipX: opts.flipX === true,
    layouts: {
      desktop: {
        x: opts.x,
        y: opts.y,
        widthFrac: 0.06,
        scale,
        flipX: opts.flipX === true,
      },
      tablet: {
        x: opts.x,
        y: opts.y,
        widthFrac: 0.06,
        scale,
        flipX: opts.flipX === true,
      },
      mobile: {
        x: opts.x,
        y: opts.y,
        widthFrac: 0.055,
        scale,
        flipX: opts.flipX === true,
      },
    },
    sprite,
    footprint: [...BUSH_FOOTPRINT],
    stand: { x: 0, y: 0.018 },
    hitRadius: 0,
    ambient: 'leaf-sway',
    groundShadow: 'ellipse',
  };
}

function largeTreeDef(
  id: string,
  opts: {
    title: string;
    x: number;
    y: number;
    side: 'left' | 'right';
    flipX?: boolean;
    scale?: number;
  },
): WorldObjectDef {
  const scale = opts.scale ?? 1;
  return {
    id,
    title: opts.title,
    icon: '🌳',
    actionLabel: 'Look',
    shape: 'hammock',
    interactive: false,
    enabled: true,
    x: opts.x,
    y: opts.y,
    widthFrac: 0.18,
    flipX: opts.flipX === true,
    layouts: {
      desktop: {
        x: opts.x,
        y: opts.y,
        widthFrac: 0.18,
        scale,
        flipX: opts.flipX === true,
      },
      tablet: {
        x: opts.x,
        y: opts.y,
        widthFrac: 0.17,
        scale,
        flipX: opts.flipX === true,
      },
      mobile: {
        x: opts.x,
        y: opts.y,
        widthFrac: 0.16,
        scale,
        flipX: opts.flipX === true,
      },
    },
    sprite: opts.side === 'left' ? { ...TREE_LEFT_SPRITE } : { ...TREE_RIGHT_SPRITE },
    footprint: [...TREE_FOOTPRINT],
    stand: { x: 0, y: 0.029 },
    hitRadius: 0,
    ambient: 'canopy-sway',
    groundShadow: 'ellipse',
  };
}

export const SPANISH_FORK_PROP_DEFS: readonly WorldObjectDef[] = [
  // Circular freestanding gazebo on the circle patio pad.
  {
    id: 'gazebo',
    title: 'Gazebo',
    icon: '🏡',
    actionLabel: 'Look',
    shape: 'hammock',
    interactive: false,
    enabled: true,
    x: 0.3,
    y: 0.76,
    widthFrac: 0.26,
    layouts: {
      desktop: {
        x: 0.3,
        y: 0.76,
        widthFrac: 0.26,
        scale: 1,
      },
      tablet: {
        x: 0.3,
        y: 0.76,
        widthFrac: 0.24,
        scale: 1,
      },
      mobile: {
        x: 0.28,
        y: 0.78,
        widthFrac: 0.22,
        scale: 1,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/gazebo-v1.png`,
      alt: 'Circular freestanding wooden backyard gazebo',
      imageWidth: 1400,
      imageHeight: 1410,
    },
    footprint: [
      { x: -0.04, y: 0.012 },
      { x: 0.04, y: 0.012 },
      { x: 0.048, y: -0.01 },
      { x: 0.02, y: -0.03 },
      { x: -0.02, y: -0.03 },
      { x: -0.048, y: -0.01 },
    ],
    stand: { x: 0, y: 0.035 },
    hitRadius: 0,
    // No occlusion split — nearby berm trees must stay fully behind the
    // freestanding gazebo (bg-half depth would let their canopies paint over it).
    groundShadow: 'ellipse',
  },
  // Swing on the right side of the grass bowl.
  {
    id: 'swingset-slide',
    title: 'Swingset with Slide',
    icon: '🛝',
    actionLabel: 'Look',
    shape: 'hammock',
    enabled: true,
    x: 0.72,
    y: 0.68,
    widthFrac: 0.28,
    flipX: true,
    layouts: {
      desktop: {
        x: 0.72,
        y: 0.68,
        widthFrac: 0.28,
        scale: 1,
        flipX: true,
      },
      tablet: {
        x: 0.72,
        y: 0.68,
        widthFrac: 0.26,
        flipX: true,
      },
      mobile: {
        x: 0.68,
        y: 0.7,
        widthFrac: 0.22,
        flipX: true,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/swingset-slide-v1.png`,
      alt: 'Wooden swingset with two swings and a slide',
      imageWidth: 1429,
      imageHeight: 784,
    },
    footprint: [
      { x: -0.06, y: 0.01 },
      { x: 0.06, y: 0.01 },
      { x: 0.055, y: -0.02 },
      { x: -0.055, y: -0.02 },
    ],
    stand: { x: 0, y: 0.035 },
    hit: { x: 0, y: -0.04 },
    hitRadius: 0.03,
    activationZone: {
      shape: 'circle',
      x: 0,
      y: 0.035,
      radius: 0.016,
    },
    groundShadow: 'soft',
  },
  // Small garden footbridge spanning the right-side dirt berm / path.
  {
    id: 'small-bridge',
    title: 'Small Bridge',
    icon: '🌉',
    actionLabel: 'Look',
    shape: 'hammock',
    interactive: false,
    enabled: true,
    x: 0.86,
    y: 0.6,
    widthFrac: 0.18,
    layouts: {
      desktop: {
        x: 0.86,
        y: 0.6,
        widthFrac: 0.18,
        scale: 1,
      },
      tablet: {
        x: 0.86,
        y: 0.6,
        widthFrac: 0.17,
        scale: 1,
      },
      mobile: {
        x: 0.84,
        y: 0.62,
        widthFrac: 0.16,
        scale: 1,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/small-bridge-v1.png`,
      alt: 'Small wooden backyard garden footbridge',
      imageWidth: 1400,
      imageHeight: 788,
    },
    footprint: [
      { x: -0.06, y: 0.012 },
      { x: 0.06, y: 0.012 },
      { x: 0.055, y: -0.018 },
      { x: -0.055, y: -0.018 },
    ],
    stand: { x: 0, y: 0.028 },
    hitRadius: 0,
    groundShadow: 'soft',
  },
  // Grill near the front of the left lawn / patio area.
  {
    id: 'gas-grill',
    title: 'Gas Grill',
    icon: '🍖',
    actionLabel: 'Look',
    shape: 'fire-pit',
    enabled: true,
    x: 0.42,
    y: 0.88,
    widthFrac: 0.09,
    layouts: {
      desktop: {
        x: 0.42,
        y: 0.88,
        widthFrac: 0.09,
        scale: 1,
      },
      tablet: {
        x: 0.42,
        y: 0.88,
        widthFrac: 0.085,
        scale: 1,
      },
      mobile: {
        x: 0.4,
        y: 0.86,
        widthFrac: 0.08,
        scale: 1,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/gas-grill-v1.png`,
      alt: 'Freestanding backyard gas grill',
      imageWidth: 1100,
      imageHeight: 1021,
    },
    footprint: [
      { x: -0.025, y: 0.008 },
      { x: 0.025, y: 0.008 },
      { x: 0.022, y: -0.012 },
      { x: -0.022, y: -0.012 },
    ],
    stand: { x: 0, y: 0.03 },
    hit: { x: 0, y: -0.03 },
    hitRadius: 0.02,
    activationZone: {
      shape: 'circle',
      x: 0,
      y: 0.03,
      radius: 0.016,
    },
    groundShadow: 'soft',
  },
  // Near-camera porch framing the yard entrance.
  {
    id: 'porch-roof',
    title: 'Porch',
    icon: '🏠',
    actionLabel: 'Look',
    shape: 'hammock',
    interactive: false,
    enabled: true,
    x: 0.5,
    y: 1,
    widthFrac: 0.72,
    layouts: {
      desktop: {
        x: 0.5,
        y: 1,
        widthFrac: 0.72,
        scale: 1,
      },
      tablet: {
        x: 0.5,
        y: 1,
        widthFrac: 0.68,
        scale: 1,
      },
      mobile: {
        x: 0.5,
        y: 0.98,
        widthFrac: 0.7,
        scale: 1,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/porch-roof-under-v1.png`,
      alt: 'Under-porch ceiling, front beam, and two posts framing the yard',
      imageWidth: 3100,
      imageHeight: 1043,
    },
    footprint: [],
    stand: { x: 0, y: -0.055 },
    hitRadius: 0,
    occlusion: {
      enabled: true,
      sourceRect: {
        x: -0.36,
        y: -0.45,
        width: 0.72,
        height: 0.45,
      },
    },
    groundShadow: 'soft',
  },
  // Five large trees along the back and right berms (behind the gazebo).
  largeTreeDef('spanish-fork-large-tree-1', {
    title: 'Big Leafy Tree',
    x: 0.22,
    y: 0.4,
    side: 'left',
    scale: 0.92,
  }),
  largeTreeDef('spanish-fork-large-tree-5', {
    title: 'Big Leafy Tree 5',
    // Right berm, in the gap between trees 3 and 4 and just outside the walk
    // polygon, so it fills the empty slope behind the swingset without ever
    // standing in the way.
    x: 0.78,
    y: 0.44,
    side: 'right',
    flipX: true,
    scale: 0.92,
  }),
  largeTreeDef('spanish-fork-large-tree-2', {
    title: 'Big Leafy Tree 2',
    x: 0.48,
    y: 0.34,
    side: 'right',
    scale: 1,
  }),
  largeTreeDef('spanish-fork-large-tree-3', {
    title: 'Big Leafy Tree 3',
    x: 0.66,
    y: 0.37,
    side: 'left',
    flipX: true,
    scale: 0.95,
  }),
  largeTreeDef('spanish-fork-large-tree-4', {
    title: 'Big Leafy Tree 4',
    x: 0.88,
    y: 0.52,
    side: 'right',
    scale: 0.9,
  }),
  // Eight bushes tucked into dirt beds and edges.
  bushDef('spanish-fork-bush-1', {
    title: 'Bush',
    x: 0.24,
    y: 0.5,
    variant: 1,
    scale: 0.95,
  }),
  bushDef('spanish-fork-bush-2', {
    title: 'Bush 2',
    x: 0.38,
    y: 0.34,
    variant: 2,
    scale: 1,
  }),
  bushDef('spanish-fork-bush-3', {
    title: 'Bush 3',
    x: 0.56,
    y: 0.33,
    variant: 1,
    flipX: true,
    scale: 0.9,
  }),
  bushDef('spanish-fork-bush-4', {
    title: 'Bush 4',
    x: 0.72,
    y: 0.36,
    variant: 2,
    scale: 1.05,
  }),
  bushDef('spanish-fork-bush-5', {
    title: 'Bush 5',
    x: 0.84,
    y: 0.46,
    variant: 1,
    flipX: true,
    scale: 0.95,
  }),
  bushDef('spanish-fork-bush-6', {
    title: 'Bush 6',
    x: 0.9,
    y: 0.66,
    variant: 2,
    scale: 1,
  }),
  bushDef('spanish-fork-bush-7', {
    title: 'Bush 7',
    x: 0.88,
    y: 0.82,
    variant: 1,
    scale: 0.85,
  }),
  bushDef('spanish-fork-bush-8', {
    title: 'Bush 8',
    x: 0.78,
    y: 0.52,
    variant: 2,
    flipX: true,
    scale: 0.9,
  }),
];
